import React from 'react';
import { observer } from 'mobx-react';

import { useGridApiRef } from '@mui/x-data-grid-pro';

import debounce from 'lodash/debounce';
import { InputAdornment } from '@mui/material';

import { StyledTextField } from 'components/Input/styles';
import SearchIcon from 'components/Icons/Search';

import { FilterType } from 'components/Grid/types';

import { UserRole } from 'core/constants';

import { useStore } from 'utils/hooks/store';
import { readerPeriod } from 'utils/formatters/timezone';

import SessionUser from 'stores/Session/SessionUser';

import { VatixError } from 'components/Error/types';

import { useTasksFilters } from 'containers/Tasks/hooks/useTasksFilters';
import { AUDITS_DEFAULT_STATUS_FILTER } from 'containers/Tasks/hooks/types';
import type { DueDateRange } from 'containers/Tasks/components/DueDatePicker';
import ActionsSchedulesTable from 'containers/Tasks/components/ActionsSchedulesTable';
import DueDatePicker from 'containers/Tasks/components/DueDatePicker';
import StatusPicker from 'containers/Tasks/components/StatusPicker';
import AssignedToPicker from 'containers/Tasks/components/AssignedToPicker';
import MoreFiltersPicker from 'containers/Tasks/components/MoreFiltersPicker';
import { AUDITS_STATUS_OPTIONS } from 'containers/Tasks/components/StatusPicker/constants';
import { FiltersContainer, VerticalDivider, ResetButton, Spacer } from 'containers/Tasks/styles';

interface AuditsTabProps {
  onError: (error: VatixError) => void;
  onRefreshCallback: (callback: Function) => void;
}

type AuditsTabContentProps = AuditsTabProps & { user: SessionUser };

const AuditsTabContent: React.FunctionComponent<AuditsTabContentProps> = ({
  onError,
  onRefreshCallback,
  user,
}): React.ReactElement => {
  const refreshRef = React.useRef<Function | null>(null);
  const gridApiRef = useGridApiRef();

  const defaultDueDate: DueDateRange = readerPeriod('month');

  const [searchQuery, setSearchQuery] = React.useState('');
  const [debouncedQuery, setDebouncedQuery] = React.useState('');

  const debouncedSetQuery = React.useMemo(() => debounce(setDebouncedQuery, 300), []);

  const temporaryFilters = React.useMemo<FilterType[]>(
    () => (debouncedQuery ? [{ field: 'query', operator: 'is', value: debouncedQuery }] : []),
    [debouncedQuery]
  );

  const {
    initialFilters,
    dueDateFilter,
    statusFilter,
    assignedToFilter,
    moreFilters,
    hasActiveFilters,
    handleDueDateChange,
    handleStatusChange,
    handleAssignedToChange,
    handleMoreFiltersChange,
    handleResetFilters,
  } = useTasksFilters({
    gridApiRef,
    refreshRef,
    user,
    defaultStatus: AUDITS_DEFAULT_STATUS_FILTER,
    defaultDueDate,
    storageKey: 'actions_audits_filters',
  });

  const handleRefreshCallback = (callback: Function): void => {
    refreshRef.current = callback;
    onRefreshCallback(callback);
  };

  return (
    <>
      <FiltersContainer>
        {user.role !== UserRole.User ? (
          <AssignedToPicker value={assignedToFilter} onChange={handleAssignedToChange} licenses="workflows" />
        ) : null}
        <DueDatePicker value={dueDateFilter} onChange={handleDueDateChange} clearable={false} />
        <StatusPicker value={statusFilter} onChange={handleStatusChange} options={AUDITS_STATUS_OPTIONS} />
        <VerticalDivider />
        <MoreFiltersPicker value={moreFilters} onChange={handleMoreFiltersChange} />
        {hasActiveFilters && <ResetButton onClick={handleResetFilters}>Reset filters</ResetButton>}
        <Spacer />
        <StyledTextField
          placeholder="Search"
          size="small"
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            debouncedSetQuery(e.target.value);
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </FiltersContainer>
      <ActionsSchedulesTable
        onError={onError}
        onRefreshCallback={handleRefreshCallback}
        gridApiRef={gridApiRef}
        temporaryFilters={temporaryFilters}
        initialFilters={initialFilters}
      />
    </>
  );
};

const ObservedAuditsTabContent = observer(AuditsTabContent);

// initialFilters is captured when the grid mounts, so mounting before session.user resolves would
// pin the grid to an empty assignedTo filter while the picker shows the current user.
const AuditsTab: React.FunctionComponent<AuditsTabProps> = (props): React.ReactElement | null => {
  const { session } = useStore();

  if (!session.user) return null;

  return <ObservedAuditsTabContent {...props} user={session.user} />;
};

export default AuditsTab;
